import Immutable from 'seamless-immutable';
import all from 'ramda/src/all';
import equals from 'ramda/src/equals';
import pipe from 'ramda/src/pipe';
import pluck from 'ramda/src/pluck';
import propEq from 'ramda/src/propEq';
import pathOr from 'ramda/src/pathOr';
import { createSelector } from 'reselect';
import CoreLocaleKeys from 'invision-core/src/locales/core.locale.keys';
import i18n from 'invision-core/src/components/i18n/i18n';
import { ActiveSubtenantsSelector } from 'invision-core/src/components/subtenants/subtenants.selectors';
import { ALL_STEPS_KEYS } from '../constants/new.connect.wizard.constants';
import { billCycleHelper } from 'invision-core/src/utilities/bill.cycle.helper';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { ShippingMethodsSelector } from 'invision-core/src/components/metadata/shippingMethods/shipping.methods.selectors';
import { OfferingOrderQuoteSelector, OfferingsSelector, OfferingOrderFinanceCreditLimitSelector, SelectedInventoryStoresSelector } from './offering.order.selectors';
import { AvailableCreditFromQuote, BillCycle, BillingAddressStateRegionProvinceValueOptions, Cart, DeliveryOptionsByPricingPlanHelper, DeliveryMethodOptionsSelector, EditOption, EditOptions, EditCart, EditSelectedFacetIds, EditSelectedOfferId, FinanceCreditFromQuoteSelector, FormattedAttributes, FormattedDeliveryDecisions, FormattedPhysicalAttributes, GetAdditionalPropertyListMapSelector, GetCartAdditionalPropertiesSelector, GetOfferAdditionalPropertiesSelector, GetOfferingOrderQuoteSelector, GetOrderAdditionalPropertiesSelector, GetPostQuoteDueTotalsSelector, GetQuoteHasInvoiceItemsSelector, GetTotalDownPaymentAmountSelector, InventoryAvailabilityRequests, IsCreditLimitBreachedFromQuote, PaymentInfo, SelectedEditOption, SelectedOfferId, TaxLocationDecisionsByPricingPlanHelper } from '../helpers/offer.ordering.wizard.selector.helper';
import { CompletedSteps, CurrentStep, CurrentStepIndex, CurrentStepIsInvalid, FormattedCityState, FormattedName, IsActiveStep, SelectedTabIndex, SkippedStep, TabPanelViewModel } from '../helpers/wizard.selector.helper';
import { currencyValuesForCountry, currencyValueOptionsForCountry } from '../helpers/customer.selectors.helpers';
import { CUSTOMER_CATEGORY } from '../../components/createCustomerPopup/create.customer.popup.constants';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { CartAdditionalPropertyFieldsSelector, OfferAdditionalPropertyFieldsSelector, OrderAdditionalPropertyFieldsSelector } from 'invision-core/src/components/metadata/codes/codes.additional.properties.selectors';
import { EMPTY_ARRAY } from '../constants/common.constants';
var NewConnectWizardStoreSelector = function NewConnectWizardStoreSelector(state) {
  return state.customercare.recoverableUiState.newConnectWizard;
};
var CartSelector = Cart([NewConnectWizardStoreSelector]);
export var CurrentStepIndexSelector = CurrentStepIndex([NewConnectWizardStoreSelector]);
export var CurrentStepSelector = CurrentStep([CurrentStepIndexSelector], ALL_STEPS_KEYS);
export var IsServiceabilityStepSelector = IsActiveStep([CurrentStepSelector], ALL_STEPS_KEYS, 0);
export var IsOffersStepSelector = IsActiveStep([CurrentStepSelector], ALL_STEPS_KEYS, 1);
export var IsDecisionsStepSelector = IsActiveStep([CurrentStepSelector], ALL_STEPS_KEYS, 2);
export var IsAttributesStepSelector = IsActiveStep([CurrentStepSelector], ALL_STEPS_KEYS, 3);
export var IsCustomerStepSelector = IsActiveStep([CurrentStepSelector], ALL_STEPS_KEYS, 4);
export var IsCheckoutStepSelector = IsActiveStep([CurrentStepSelector], ALL_STEPS_KEYS, 5);
export var CompletedStepsSelector = CompletedSteps([NewConnectWizardStoreSelector]);
export var EditCartSelector = EditCart([NewConnectWizardStoreSelector]);
export var EditOptionsSelector = EditOptions([EditCartSelector]);
var SelectedEditOptionSelector = SelectedEditOption([EditCartSelector]);
export var BillCycleSelector = BillCycle([EditCartSelector]);
export var EditOptionSelector = EditOption([SelectedEditOptionSelector, EditOptionsSelector]);
export var EditSelectedFacetIdsSelector = EditSelectedFacetIds([EditCartSelector]);
export var EditSelectedOfferIdSelector = EditSelectedOfferId([EditCartSelector]);
export var PaymentInfoSelector = PaymentInfo([CartSelector]);
export var PaymentInfoEditSelector = PaymentInfo([EditCartSelector]);
export var SkippedStepSelector = SkippedStep([NewConnectWizardStoreSelector]);
export var CurrentStepIsInvalidSelector = CurrentStepIsInvalid([NewConnectWizardStoreSelector]);
export var SelectedTabIndexSelector = SelectedTabIndex([NewConnectWizardStoreSelector]);
export var TabPanelViewModelSelector = TabPanelViewModel([SelectedTabIndexSelector]);
export var BillingAddressStateRegionProvinceValueOptionsSelector = BillingAddressStateRegionProvinceValueOptions([PaymentInfoEditSelector, MetadataCodeTypeSelector(CODES.AddressCountry), MetadataCodeTypeSelector(CODES.AddressStateProvinceRegion)]);
export var PaymentInfoEditAutoPaySelector = createSelector([PaymentInfoEditSelector], function (paymentInfoEdit) {
  return paymentInfoEdit && paymentInfoEdit.ConvergentBillerPaymentInstrumentAccounts ? pipe(pluck('AutoPay'), all(equals(true)))(paymentInfoEdit.ConvergentBillerPaymentInstrumentAccounts) : false;
});
export var CustomerInfoSelector = createSelector([CartSelector], function (cart) {
  return cart.customerInfo.asMutable();
});
var emptyCustomerInfo = {};
export var EditCustomerInfoSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.customerInfo !== undefined ? editCart.customerInfo.asMutable({
    deep: true
  }) : emptyCustomerInfo;
});
export var EditCustomerSubscriberCurrency = createImmutableSelector([EditCustomerInfoSelector], function (editCustomerInfo) {
  return editCustomerInfo.SubscriberCurrency;
});
export var EditCustomerInfoIsCategoryValidSelector = createSelector([EditCustomerInfoSelector], function (editCustomerInfo) {
  var customerCategory = editCustomerInfo.Category;
  return customerCategory === CUSTOMER_CATEGORY.RESIDENTIAL || customerCategory === CUSTOMER_CATEGORY.COMMERCIAL;
});
export var EditCustomerInfoSubtenantSelector = createSelector([EditCustomerInfoSelector], function (editCustomer) {
  return editCustomer.SubtenantId ? [editCustomer.SubtenantId] : null;
});
export var FormattedNameSelector = FormattedName([CustomerInfoSelector]);
export var FormattedCityStateSelector = FormattedCityState([CustomerInfoSelector]);
export var SelectedOfferIdSelector = SelectedOfferId([CartSelector]);
export var SelectedOfferSelector = createSelector([SelectedOfferIdSelector, OfferingsSelector], function (selectedOfferId, offeringsMap) {
  return offeringsMap[selectedOfferId];
});
export var FormattedAttributesSelector = FormattedAttributes([EditCartSelector]);
export var FormattedPhysicalAttributesSelector = FormattedPhysicalAttributes([EditCartSelector, SelectedOfferIdSelector, SelectedInventoryStoresSelector]);
export var InventoryAvailabilityRequestsSelector = InventoryAvailabilityRequests([FormattedPhysicalAttributesSelector, EditCartSelector]);
var EMPTY_ATTRIBUTE_GROUPS = Immutable([]);
export var EditAttributeGroupsSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.editAttributeGroups ? editCart.editAttributeGroups.asMutable({
    deep: true
  }) : EMPTY_ATTRIBUTE_GROUPS;
});
export var DeliveryDecisionsSelector = createImmutableSelector([EditCartSelector], function (editCart) {
  return editCart.deliveryDecisions || EMPTY_ARRAY;
});
export var ServiceTaxDecisionsSelector = createImmutableSelector([EditCartSelector], function (editCart) {
  return editCart.serviceTaxDecisions || EMPTY_ARRAY;
});
export var DeliveryOptionsByPricingPlanSelector = DeliveryOptionsByPricingPlanHelper([DeliveryDecisionsSelector, ShippingMethodsSelector]);
export var FormattedDeliveryDecisionsSelector = FormattedDeliveryDecisions([DeliveryDecisionsSelector]);
export var TaxLocationDecisionsByPricingPlanSelector = TaxLocationDecisionsByPricingPlanHelper([ServiceTaxDecisionsSelector]);
export var EditPhysicalAttributeGroupsSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.editPhysicalAttributeGroups ? editCart.editPhysicalAttributeGroups.asMutable({
    deep: true
  }) : EMPTY_ATTRIBUTE_GROUPS;
});
export var EditSelectedOfferingChargeTypeIdSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.selectedOfferingChargeTypeIds;
});
export var ConvergentBillerAccountsSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.convergentBillerAccounts;
});
export var CreatedCustomerSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.customer;
});
export var IsCreatingOrUpdatingCustomerSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.isCreatingOrUpdatingCustomer;
});
export var IsFetchingBillCyclesSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.isFetchingBillCycles;
});
export var IsFetchingDataSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.isFetchingData;
});
export var ViewLastAttemptErrorSelector = createSelector([EditCartSelector], function (editCart) {
  return editCart.lastAttemptError;
});
export var CreateCountryCurrencyValueOptions = createSelector([EditCustomerInfoSelector, MetadataCodeTypeSelector(CODES.CountryCurrency)], function (customer, currencies) {
  return currencyValueOptionsForCountry(customer.HomeCountry, currencies);
});
export var DefaultCountryCurrencySelector = createSelector([EditCustomerInfoSelector, MetadataCodeTypeSelector(CODES.CountryCurrency)], function (customer, currencies) {
  var countryCurrencies = currencyValuesForCountry(customer.HomeCountry, currencies) || [];
  return countryCurrencies.find(function (currencyOption) {
    return currencyOption.IsDefault;
  });
});
export var CurrentCustomerIsExternallyManagedARSelector = createSelector([CustomerInfoSelector, MetadataCodeTypeSelector(CODES.SubscriberType)], function (currentCustomer, subscriberTypes) {
  var subscriberType = subscriberTypes.find(function (subscriberType) {
    return subscriberType.Value === currentCustomer.SubscriberTypeCode;
  });
  return !!(subscriberType && subscriberType.AdditionalProperties.find(propEq('externally_managed_accounts_receivable', 'Key')).Value === 'True');
});
export var NewConnectCandidateBillCyclesSelector = createSelector([EditCustomerInfoSelector], function (customer) {
  return customer.candidateBillCycles.map(billCycleHelper.addTranslatedDueDates);
});
export var HasCustomerInfoSelector = createSelector([CustomerInfoSelector, FormattedNameSelector, FormattedCityStateSelector], function (customerInfo, formattedName, formattedCityState) {
  return !!(formattedName || customerInfo.addressLine1 || customerInfo.addressLine2 || formattedCityState || customerInfo.postalCode);
});
export var IsPopoutableSelector = createSelector([IsServiceabilityStepSelector, SelectedOfferIdSelector], function (isServiceabilityStep, selectedOfferId) {
  return isServiceabilityStep && selectedOfferId === null;
});
export var GetSubtenantNameSelector = createSelector([CustomerInfoSelector, ActiveSubtenantsSelector], function (customerInfo, currentSubtenants) {
  if (customerInfo.SubtenantId) {
    var subtenant = currentSubtenants.find(function (subtenant) {
      return subtenant.Id.Value === customerInfo.SubtenantId.Value;
    });
    return subtenant ? subtenant.Name : i18n.translate(CoreLocaleKeys.NOT_APPLICABLE);
  } else {
    return i18n.translate(CoreLocaleKeys.NOT_APPLICABLE);
  }
});
export var OfferQuoteSelector = GetOfferingOrderQuoteSelector([OfferingOrderQuoteSelector]);
export var OfferQuoteFinanceCreditLimitSelector = OfferingOrderFinanceCreditLimitSelector;
export var AvailableCreditLimitFromQuoteSelector = AvailableCreditFromQuote([OfferQuoteSelector]);
export var IsCreditLimitBreachedFromQuoteSelector = IsCreditLimitBreachedFromQuote([OfferQuoteSelector]);
export var AvailableFinanceCreditFromQuoteSelector = FinanceCreditFromQuoteSelector([OfferQuoteFinanceCreditLimitSelector]);
export var TotalDownPaymentSelector = GetTotalDownPaymentAmountSelector([OfferQuoteSelector]);
export var CreatedCustomerCreditLimitSelector = createImmutableSelector([CreatedCustomerSelector, OfferQuoteSelector], function (createdCustomer, offerQuoteSelector) {
  return pathOr(null, ['Subscriber', 'CreditCheckClassification', 'CreditLimit'], createdCustomer) || offerQuoteSelector && offerQuoteSelector.CreditLimit;
});
export var PostQuoteDueTotalsSelector = GetPostQuoteDueTotalsSelector([OfferQuoteSelector]);
export var QuoteHasInvoiceItemsSelector = GetQuoteHasInvoiceItemsSelector([OfferQuoteSelector]);
export var CartAdditionalPropertiesSelector = GetCartAdditionalPropertiesSelector([NewConnectWizardStoreSelector]);
export var CartAdditionalPropertyListMapSelector = GetAdditionalPropertyListMapSelector([CartAdditionalPropertiesSelector, CartAdditionalPropertyFieldsSelector]);
export var OrderAdditionalPropertiesSelector = GetOrderAdditionalPropertiesSelector([NewConnectWizardStoreSelector]);
export var OfferAdditionalPropertiesSelector = GetOfferAdditionalPropertiesSelector([NewConnectWizardStoreSelector]);
export var OrderAdditionalPropertyListMapSelector = GetAdditionalPropertyListMapSelector([OrderAdditionalPropertiesSelector, OrderAdditionalPropertyFieldsSelector]);
export var OfferAdditionalPropertyListMapSelector = GetAdditionalPropertyListMapSelector([OfferAdditionalPropertiesSelector, OfferAdditionalPropertyFieldsSelector]);
export var NewConnectOfferDeliveryMethodOptionsSelector = DeliveryMethodOptionsSelector([ShippingMethodsSelector, EditCustomerSubscriberCurrency]);